// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelChart
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS funnel visualization for a shared funnel
 * analysis result. The component presents each step's conversion bar plus the
 * overall conversion summary for onboarding and checkout flows.
 */

import type { FunnelResult } from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
} from "@geenius/ui/solidjs";
import { type Component, For, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS funnel chart.
 *
 * @property result Funnel analysis result to visualize.
 * @property error Optional error value rendered instead of the funnel body.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the chart wrapper.
 */
export interface FunnelChartProps {
  result: FunnelResult;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS funnel chart for a shared funnel result.
 *
 * @param props Funnel result payload for the chart.
 * @returns A funnel chart element, or an empty-state element when no steps exist.
 */
export const FunnelChart: Component<FunnelChartProps> = (props) => {
  const copy = createAnalyticsCopy();

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader class="pb-0">
          <CardTitle class="text-sm" data-analytics="funnel-title">
            {props.result.name}
          </CardTitle>
        </CardHeader>
        <CardContent class="pt-4">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Show
      when={props.result.steps.length > 0}
      fallback={
        <Card class={props.class}>
          <CardContent class="pt-6">
            <EmptyState
              bordered={false}
              compact
              title={copy.t("analytics.funnel.empty")}
            />
          </CardContent>
        </Card>
      }
    >
      <Card class={props.class}>
        <CardHeader class="pb-0">
          <CardTitle class="text-sm" data-analytics="funnel-title">
            {props.result.name}
          </CardTitle>
        </CardHeader>
        <CardContent class="grid gap-3 pt-4">
          <div class="flex flex-col gap-3" data-analytics="funnel-steps">
            <For each={props.result.steps}>
              {(step, index) => (
                <div class="grid gap-2" data-analytics="funnel-step">
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-sm font-medium text-foreground">
                      {step.name}
                    </span>
                    <Badge variant="secondary">{step.count}</Badge>
                  </div>
                  <Progress value={step.conversionRate} />
                  <Show when={index() > 0}>
                    <span
                      class="text-xs text-muted-foreground"
                      data-analytics="funnel-dropoff"
                    >
                      {copy.t("analytics.funnel.dropoff", {
                        percentage: step.dropoffRate.toFixed(1),
                      })}
                    </span>
                  </Show>
                </div>
              )}
            </For>
          </div>
          <div
            class="flex items-center justify-between gap-2 text-sm text-muted-foreground"
            data-analytics="funnel-summary"
          >
            <span>
              {copy.t("analytics.funnel.overall", {
                percentage: props.result.overallConversionRate.toFixed(1),
              })}
            </span>
            <span>
              {props.result.totalConversions}/{props.result.totalEntries}
            </span>
          </div>
        </CardContent>
      </Card>
    </Show>
  );
};
